/**
 * Custom hook that wraps useApiClient with automatic API version detection
 * Detects API version based on URL parameter (accesstoken vs token + context params)
 */

import { type ApiClient, useApiClient } from "@/api";
import { useRampsStore } from "@/stores/use-ramps-store";
import { extractWalletAddressFromJwt } from "@/utils/jwt-decoder";

interface UseApiClientOptions {
	authToken: string;
	walletAddress?: string;
}

/**
 * Hook that automatically detects API version from URL parameters and creates API client
 * - accesstoken parameter → v5 API (OAuth, wallet address as query param)
 * - token + URL context params → v2 API (JWT, context from URL)
 * - token without URL context → v3 API (JWT, context from prepare endpoint)
 * - API version is cached in store after first detection
 */
export function useApiClientWithAutoVersion(
	options?: Partial<UseApiClientOptions>,
): { apiClient: ApiClient; apiVersion: string; walletAddress: string | null } {
	const store = useRampsStore();

	// Get auth token from options or fallback to store
	const authToken = options?.authToken || store.token;

	// Use API version from store (detected once in calculator on first load)
	const apiVersion = store.apiVersion || "v3"; // Fallback to v3 for backward compatibility

	// Extract wallet address based on API version
	// V5 (OAuth): Use wallet address from options/store (passed as query param)
	// V2/V3 (JWT): Extract wallet address from JWT token's 'sub' claim
	let walletAddress: string | null = null;

	if (apiVersion === "v5") {
		// OAuth flow - wallet address comes from external source (URL param or store)
		walletAddress = options?.walletAddress || store.walletAddress || null;
	} else {
		// JWT flow (v2/v3) - extract wallet address from token
		// Only attempt extraction if we have a token
		if (authToken) {
			walletAddress = extractWalletAddressFromJwt(authToken);
		}
	}

	const apiClient = useApiClient({
		authToken,
		apiVersion,
		walletAddress: walletAddress || undefined,
		transactionId: store.transactionId || undefined,
	});

	return {
		apiClient,
		apiVersion,
		walletAddress,
	};
}
